/* WL DISC PICKER — bulk add, as a bag EDITOR
   ============================================================================
   The old way in was a search box that added one disc at a time. Fine for
   "I want a Firebird", useless for "let me build a bag", which is the thing
   the whole app is for.

   THE KEY DECISION: this list is an EDITOR, not a loader.
   Discs already in the bag come pre-ticked. Ticking adds, unticking removes,
   and nothing happens until Update is pressed. That is what makes the
   keep-or-clear question disappear -- there is no destructive load to warn
   anyone about, because opening the picker cannot lose you anything.

   ONE EXCEPTION, ADDED 22 AUG: the example bag. See onDemoBag() below. A bag
   the visitor has not built is not work, and pre-ticking it makes them undo
   our suggestions before they can start.

   Grouped by disc type and sorted by speed inside each group, because that is
   how people actually audit a bag: "what am I missing in mids". Everything
   else is a re-sort of the same list.

   Deliberately NOT here:
     * stock filtering. This ships to anyone, not just a retailer -- stock is a
       tenant field, not a default. A manufacturer tenant has no stock at all.
     * a brand filter of its own. There IS a brand rail in here now, but it is
       the shared one from brands.js, mounted -- not a second implementation.
       (That line used to read "brand belongs on the rail above the chart".
       Ryan killed the rail above the chart: it "will take away from the flight
       chart experience". The rail moved in here; this list follows it.)

   Same architecture as the map: read state from the URL hash, write state to
   the URL hash, let the chart core react. No shared globals, nothing reaching
   into the 50KB engine.
   ========================================================================= */
(function () {
"use strict";
var FD = window.__FDS__; if (!FD || !FD.molds) return;
var M = FD.molds;

var TYPES = [
  { k: "DD", lbl: "Distance drivers" },
  { k: "FD", lbl: "Fairway drivers" },
  { k: "MR", lbl: "Midranges" },
  { k: "PA", lbl: "Putt & approach" }
];

function slug(d) { return (d.v + "-" + d.m).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
function b1(n) { return (Math.round(10 * n) / 10).toString(); }
function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
function base(t) { var i = t.indexOf("~"); return i < 0 ? t : t.slice(0, i); }
function typeOf(d) {
  if (window.__FDS_MAP__) return window.__FDS_MAP__.bandOf(d);
  /* Same rule as grid.js bandOf, for the case where the map has not loaded.
     Speed decides, the label does not -- fairway drivers stop at 9. */
  /* Same rule as grid.js bandOf(), minus the family lookup -- this branch only
     runs when the map has not loaded, and a variant landing in the wrong row
     for one frame is cheaper than a second copy of the family table. */
  return d.s >= 10 ? "DD" : d.s >= 6 ? "FD" : d.s >= 4 ? (d.s === 4 && d.g <= 3 ? "PA" : "MR") : "PA";
}
var BY = {}; M.forEach(function (d) { BY[slug(d)] = d; });

function hashTokens() {
  var m = /(?:^|#|&)d=([^&]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]).split(",").filter(function (s) { return BY[base(s)]; }) : [];
}

/* Write the whole bag at once. The per-disc throw suffix on any token that
   survives is carried through untouched -- a bulk edit must not quietly reset
   somebody's forehand overrides. */
function writeBag(tokens) {
  var h = location.hash.replace(/^#/, "");
  var keep = h.split("&").filter(function (p) { return p && p.indexOf("d=") !== 0; });
  location.hash = (tokens.length ? ["d=" + tokens.join(",")] : []).concat(keep).join("&");
}

var SORTS = {
  type:  null,                                                     /* grouped */
  speed: function (a, b) { return b.s - a.s || a.m.localeCompare(b.m); },
  brand: function (a, b) { return a.v.localeCompare(b.v) || b.s - a.s; },
  name:  function (a, b) { return a.m.localeCompare(b.m); },
  stability: function (a, b) { return (a.tu + a.f) - (b.tu + b.f) || a.m.localeCompare(b.m); }
};


/* ---------------------------------------------------------------- SUGGESTIONS
   WHY THE PICKER NO LONGER OPENS ON A CATALOGUE.
   It used to open on all 564 distance drivers with the Raketen -- a speed 15
   -- at the top, and 172 of them needed an arm almost nobody has. Ryan spotted
   it immediately. A list sorted by speed is a list sorted by "least likely to
   help you", and opening on it makes the first screen of the app a wall.

   He also asked whether it should stay quiet until the bag has five or six
   discs in it and only then suggest. The answer settled 7 Sep, after a wrong
   turn worth recording: an empty bag suggests NOTHING.

   Gap maths on an empty bag is not a smaller version of gap maths on a bag --
   every cell is uncovered, so it ranks the whole catalogue by hole-filling and
   surfaces oddities (Notion, Cobra, Lynx, Mystere). The fix attempted on 3 Sep
   was to hand the empty bag a curated starter set instead. Ryan killed that
   too, and for the better reason: "this tool is NOT designed for beginners...
   it is designed to catch advanced players who want to be able to compare
   discs to each other." Prescribing a first bag misreads who is holding the
   phone. The starter pack is a BUTTON in the bag panel now, not a default.

   So the seam is at zero discs: no bag, no suggestions, just the catalogue and
   the search. Suggestions exist to answer "what shape is missing", and that
   question needs a bag to be missing something from.

   THE ORDER IS PUTTER FIRST. My first attempt led with five distance-driver
   gaps, which is the same mistake in a new hat. Bags are built outward from
   the basket: what you putt with, then what you throw into the green, then the
   fairway, then distance. So the bands are walked PA, MR, FD, DD and inside a
   band the STRAIGHT centre is filled before the edges.
*/
var BAND_ORDER = ["PA", "MR", "FD", "DD"];

/* WHICH DISC TO OFFER FOR A HOLE, once the hole is chosen.
   Every candidate in a cell already throws the same shot -- that is what a
   cell IS -- so ordering them by "easiest to throw" alone answers a question
   that is mostly already settled, and it surfaced a Yikun Cloudfang and a Mint
   Salamander to a bag of eight Innovas. Technically correct, useless advice.

   So the order is: a brand you already throw, then how well sourced the flight
   numbers are, then the brands that make the most moulds, and only then the
   numbers as a tiebreak. Buyability is NOT part of it any more -- see the note
   in chooser(). */
function brandRank() {
  var r = {};
  if (window.__FDS_BRANDS__) {
    window.__FDS_BRANDS__.rank.forEach(function (b, i) { r[b.v] = i; });
  }
  return r;
}

/* Every label in this app is directional ("finishes right") because that is
   what the map measures. Nobody searches that -- they search "flippy", which
   returned NOTHING when matching only mould name and brand. These map a word
   to a test on the flight numbers, so they cannot drift from what is drawn. */
var CONF = { manufacturer: 0, fds: 0, retailer: 1, aggregator: 2 };

var VOCAB = [
  { w: ["flippy", "flip", "understable", "turnover", "turn over", "flip up", "rollup", "roller"],
    f: function (d) { return d.tu <= -2; } },
  { w: ["very understable", "super flippy", "noodle"],
    f: function (d) { return d.tu <= -3 && d.f <= 1; } },
  { w: ["overstable", "meat hook", "meathook", "hyzer", "utility", "wind", "windy", "headwind"],
    f: function (d) { return d.tu + d.f >= 3; } },
  { w: ["very overstable", "super overstable", "forehand", "flex"],
    f: function (d) { return d.tu + d.f >= 4; } },
  { w: ["straight", "neutral", "dead straight"],
    f: function (d) { return Math.abs(d.tu + d.f) <= 1; } },
  { w: ["beginner", "beginner friendly", "easy", "slow", "new player", "starter"],
    f: function (d) { return d.s <= 7 && d.f <= 2; } },
  { w: ["distance", "bomber", "far", "max distance"],
    f: function (d) { return d.s >= 10; } },
  { w: ["putter", "putt"], f: function (d) { return typeOf(d) === "PA"; } },
  { w: ["midrange", "mid"], f: function (d) { return typeOf(d) === "MR"; } },
  { w: ["fairway", "control"], f: function (d) { return typeOf(d) === "FD"; } }
];

function vocabOf(s) {
  s = s.trim().toLowerCase();
  if (!s) return null;
  /* Longest phrase first, so "very understable" is not eaten by
     "understable" and "very overstable" is not eaten by "overstable". */
  var best = null, len = -1;
  VOCAB.forEach(function (g) {
    g.w.forEach(function (w) {
      if (w.length > len && (s === w || s.indexOf(w) >= 0)) { best = g; len = w.length; }
    });
  });
  return best;
}


function chooser(bagBrands, rank) {
  return function (a, b) {
    var ma = bagBrands[a.v] ? 0 : 1, mb = bagBrands[b.v] ? 0 : 1;
    if (ma !== mb) return ma - mb;
    /* Buyability ranking removed 29 Aug -- suggestions are the BEST disc, never
       the stocked one, and it made this list disagree with the bouquet. In its
       place: how well sourced the flight numbers are. */
    /* Same rule as the bouquet, reading the same published target, so the two
       engines cannot drift. Was `a.f - b.f` -- "prefer less fade" -- which is
       a fixed understable bias applied to every arm alike. Ryan, 29 Aug: the
       slider should decide. Ranked above provenance: suiting the player's arm
       beats a better-sourced number. Falls back to a neutral target when the
       chart has not loaded, so this module still stands alone. */
    /* Fallback is the measured catalogue mean turn+fade (1.10), not a
       guess, for the case where chart.js has not published power yet. */
    var P = window.__FDS_POWER__, want = P ? P.wantStab() : 1.1;
    var sa = Math.round(Math.abs((a.tu + a.f) - want) * 2),
        sb = Math.round(Math.abs((b.tu + b.f) - want) * 2);
    if (sa !== sb) return sa - sb;
    var ca = CONF[a.src] === void 0 ? 3 : CONF[a.src],
        cb = CONF[b.src] === void 0 ? 3 : CONF[b.src];
    if (ca !== cb) return ca - cb;
    var ra = rank[a.v] === void 0 ? 999 : rank[a.v];
    var rb = rank[b.v] === void 0 ? 999 : rank[b.v];
    if (ra !== rb) return ra - rb;
    /* Fade dropped from this tail: it is the stability rule above, done badly
       and a second time. What is left is genuinely about ease -- more glide
       stays airborne with less effort, slower needs less arm. */
    return (b.g - a.g) || (a.s - b.s) || a.m.localeCompare(b.m);
  };
}

/* NO STARTER SPREAD. Removed 7 Sep, one session after it went in.
   The empty-bag block briefly offered a curated beginner set (Aviar, Shark,
   Leopard3, Valkyrie). Ryan killed it: "this tool is NOT designed for
   beginners... it is designed to catch advanced players who want to be able to
   compare discs to each other." A first screen that prescribes a first bag
   tells the player we think they are new, which is the opposite of the pitch.
   The starter pack survives as a BUTTON in the bag panel (BAGS.starter in
   chart.js) -- opt in, one tap, gone if you never want it.
   So: nothing bagged, nothing suggested. Suggestions are for a bag that
   exists, where "what shape is missing" is a real question. */

/* What a suggestion is FOR, in the words the map already uses. "Overstable"
   tells you what the disc is; "fills: finishes right, fairway" tells you why
   it is being offered, which is the only question a suggestion has to answer.
   A starter row answers a different question -- what job it does in the bag --
   so it says the role rather than the hole. */
function sugNote(o) {
  if (o.role) return o.role;
  var MAP = window.__FDS_MAP__;
  var lbl = MAP && MAP.label ? MAP.label(o.col) : "";
  var band = { PA: "putt & approach", MR: "midrange", FD: "fairway", DD: "distance" }[o.band];
  return lbl ? lbl + " · " + band : band;
}

/* Suggestions used to switch OFF when a brand was picked (the guard required
   !brand), so choosing Innova hid the block instead of narrowing it. `cells`
   derives from byCell, so a shape the brand makes no mould for simply produces
   no suggestion rather than an empty row. */
function sugPool() {
  return pool().filter(function (d) { return !brand || d.v === brand; });
}

function suggestions(limit, emptyBag) {
  var MAP = window.__FDS_MAP__;
  if (!MAP) return [];
  var have = MAP.gaps();
  var bag = {}; hashTokens().forEach(function (t) { bag[base(t)] = 1; });
  /* Nothing ticked, nothing suggested. Gap maths on an empty bag is not a
     smaller version of gap maths on a real bag -- with every cell uncovered it
     ranks the whole catalogue by hole-filling and surfaces oddities, and a
     curated set in its place prescribes a beginner's bag to a player who came
     here to compare discs. Neither is what this screen is for.
     "Empty" is the TICKED list, not the hash: the example bag sits in the URL
     but is deliberately not pre-ticked (onDemoBag), so a visitor who has
     chosen nothing has an empty bag even while the chart shows twelve discs. */
  if (emptyBag === void 0) emptyBag = !Object.keys(bag).length;
  if (emptyBag) return [];
  var bagBrands = {};
  pool().forEach(function (d) { if (bag[slug(d)]) bagBrands[d.v] = 1; });
  var pick = chooser(bagBrands, brandRank());

  /* Bucket the whole pool by the cell each disc lands in, so choosing a
     candidate for a hole is a lookup rather than a scan per hole. */
  var byCell = {};
  sugPool().forEach(function (d) {
    if (bag[slug(d)]) return;
    var c = MAP.cellOf(d);
    (byCell[c.band + "|" + c.col] = byCell[c.band + "|" + c.col] || []).push(d);
  });

  /* Centre-out: the straight shot in a band is worth more than its edges,
     because a bag with no straight putter has a bigger problem than a bag with
     no hard-left putter. */
  var cells = Object.keys(byCell).map(function (k) {
    var p = k.split("|");
    return { band: p[0], col: +p[1], key: k };
  });
  var mid = {};
  cells.forEach(function (c) {
    mid[c.band] = Math.max(mid[c.band] || 0, c.col);
  });

  var out = [];
  BAND_ORDER.forEach(function (b) {
    cells.filter(function (c) { return c.band === b && !have[c.key]; })
      .sort(function (x, y) {
        return Math.abs(x.col - mid[b] / 2) - Math.abs(y.col - mid[b] / 2) || x.col - y.col;
      })
      .forEach(function (c) {
        var best = byCell[c.key].slice().sort(pick)[0];
        if (best) out.push({ d: best, band: b, col: c.col });
      });
  });

  /* One pass per band before a second from any band, so an empty bag gets a
     spread rather than four putters. */
  var rounds = [], seen = {};
  for (var r = 0; r < 4 && out.length; r++) {
    BAND_ORDER.forEach(function (b) {
      var hit = out.filter(function (o) { return o.band === b && !seen[slug(o.d)]; })[r];
      if (hit) { seen[slug(hit.d)] = 1; rounds.push(hit); }
    });
  }
  return rounds.slice(0, limit || 8);
}

var open = false, picked = null, q = "", sort = "type", gapsOnly = false, root = null;

/* Brand is picker-local view state, same as sort. It resets on close: the
   picker is a place you come to solve one problem, and coming back to it
   still filtered to Kastaplast from three edits ago is a trap. */
var brand = "", rail = null, brandAsked = false;

/* Has this visitor told the chart how far they throw? The engine keeps the
   answer in the URL as p=, so the hash is the source of truth rather than a
   flag this module invents. skipped is remembered for the session only: asking
   twice in one sitting is nagging, asking again tomorrow is fair. */
var skipped = false, calSet = false;
function calibrated() {
  /* NOT just "is there a p= in the hash". The shell defaults the distance to
     110 on load and the engine writes that back to the URL, so by the time
     this module runs every visit looks calibrated. The shell records the hash
     it was ENTERED with in window.__FDS_ENTRY__ precisely so this question can
     still be answered; once the visitor touches the control themselves,
     calSet flips and the panel stops asking. */
  if (calSet) return true;
  var e = window.__FDS_ENTRY__;
  return e ? !!e.calibrated : /(?:^|#|&)p=\d/.test(location.hash);
}

function pool() {
  return M.filter(function (d) { return d.s !== null && d.s !== void 0 && d.tu !== null; });
}

function matches(d, have) {
  if (brand && d.v !== brand) return false;
  if (q) {
    /* A stability word describes a SHAPE, so it filters on the numbers. Any
       other search is still a plain name/brand match -- someone who types
       "Firebird" wants the Firebird, and someone who types "flippy Innova"
       gets Innova's understable moulds because the brand rail handles the
       brand half. A word that matches both (a mould actually named "Roller")
       keeps the name hit too, so nothing becomes unfindable. */
    var vg = vocabOf(q);
    var hay = (d.m + " " + d.v).toLowerCase();
    var named = hay.indexOf(q.toLowerCase()) >= 0;
    if (vg) { if (!vg.f(d) && !named) return false; }
    else if (!named) return false;
  }
  if (gapsOnly && have && window.__FDS_MAP__) {
    if (picked[slug(d)]) return true;              /* never hide what is ticked */
    return window.__FDS_MAP__.fillsAGap(d, have);
  }
  return true;
}

function row(d, note) {
  var s = slug(d), on = !!picked[s];
  return '<button type="button" class="fdsfc-pk__row" data-slug="' + esc(s) + '"' +
    (on ? " data-on" : "") + ' role="checkbox" aria-checked="' + on + '">' +
    '<i aria-hidden="true">' + (on ? "&#10003;" : "") + "</i>" +
    "<b>" + esc(d.m) + "</b><span>" + esc(d.v) + "</span>" +
    "<em>" + b1(d.s) + " / " + b1(d.g) + " / " + b1(d.tu) + " / " + b1(d.f) + "</em>" +
    "<u>" + esc(note || d.st || "") + "</u></button>";
}

function render() {
  if (!root) return;
  var have = window.__FDS_MAP__ ? window.__FDS_MAP__.gaps() : null;
  var all = pool().filter(function (d) { return matches(d, have); });

  var body = "";
  /* Shown ONCE, on the first render after the brand was inherited from the
     map -- not every render, and never when the visitor picked the brand on
     the rail themselves, because then nobody needs telling. */
  if (brandAsked && brand) {
    body += '<div class="fdsfc-pk__carry"><p>Still just <b>' + esc(brand) +
      '</b>, carried over from the chart. Suggestions below are ' + esc(brand) +
      ' moulds only.</p><button type="button" class="fdsfc-pk__carryall">Show all brands</button></div>';
  }
  /* Whatever is already bagged goes first, always. You cannot edit a bag you
     have to scroll to find. */
  var inBag = all.filter(function (d) { return picked[slug(d)]; });
  var rest  = all.filter(function (d) { return !picked[slug(d)]; });
  if (inBag.length) {
    inBag.sort(SORTS.speed);
    body += '<div class="fdsfc-pk__grp"><h4>In your bag<span>' + inBag.length + "</span></h4>" +
      inBag.map(function (d) { return row(d); }).join("") + "</div>";
  }
  /* SUGGESTIONS SIT ABOVE THE CATALOGUE, NOT INSTEAD OF IT. The list still
     holds every mould -- someone who came for a Firebird finds it by typing.
     What changed is what the first screen offers when you have not typed
     anything: the shapes the map does not see yet, rather than the fastest discs
     ever approved. */
  var sug = [];
  if (!q && !gapsOnly && sort === "type" && inBag.length) {
    var inBagSlugs = {}; inBag.forEach(function (d) { inBagSlugs[slug(d)] = 1; });
    /* Up to eight: by the time there is a bag, the holes are specific and
       worth listing. An empty bag gets no block at all -- see SUGGESTIONS. */
    sug = suggestions(8, false).filter(function (o) { return !inBagSlugs[slug(o.d)]; });
    if (sug.length) {
      /* Name the brand in the heading. "Shapes not in your bag yet" is a
         claim about the whole sport; once the list is narrowed it is only a
         claim about one catalogue, and saying so is the difference between a
         verdict and a lie. */
      var bIn = brand ? " " + esc(brand) : "";
      body += '<div class="fdsfc-pk__grp fdsfc-pk__grp--sug"><h4>' +
        "Shapes not in your" + bIn + " bag yet" +
        "<span>" + sug.length + "</span></h4>" +
        '<p class="fdsfc-pk__sugwhy">' +
        "One disc for each shape the map does not see yet, easiest to throw first. Working out from the basket. Shown for the throw you have selected \u2014 a shape missing backhand may already be covered forehand." + (brand ? " Only shots " + esc(brand) + " makes a mould for." : "") +
        "</p>" +
        sug.map(function (o) { return row(o.d, sugNote(o)); }).join("") + "</div>";
      var sugSlugs = {}; sug.forEach(function (o) { sugSlugs[slug(o.d)] = 1; });
      rest = rest.filter(function (d) { return !sugSlugs[slug(d)]; });
    }
  }

  if (sort === "type") {
    TYPES.forEach(function (t) {
      var g = rest.filter(function (d) { return typeOf(d) === t.k; }).sort(SORTS.speed);
      if (!g.length) return;
      body += '<div class="fdsfc-pk__grp"><h4>' + t.lbl + "<span>" + g.length + "</span></h4>" +
        g.map(function (d) { return row(d); }).join("") + "</div>";
    });
  } else {
    rest.sort(SORTS[sort]);
    body += '<div class="fdsfc-pk__grp">' + rest.map(function (d) { return row(d); }).join("") + "</div>";
  }
  if (!all.length) {
    /* Name the way out that actually applies. "Turn off gaps-only" is useless
       advice when the reason nothing matched is that the brand filter is on. */
    var outs = [];
    if (brand) outs.push("pick All brands");
    if (gapsOnly) outs.push("turn off “only discs that fill a gap”");
    body = '<p class="fdsfc-pk__none">Nothing matches. Try a different search' +
      (outs.length ? ", or " + outs.join(", or ") + "." : ".") + "</p>";
  }

  root.querySelector(".fdsfc-pk__list").innerHTML = body;
  var ca = root.querySelector(".fdsfc-pk__carryall");
  if (ca) ca.addEventListener("click", function () {
    brand = ""; brandAsked = false; if (rail) rail.clear(); render();
  });
  root.querySelector(".fdsfc-pk__count").textContent =
    all.length + " of " + pool().length + " discs" + (brand ? " · " + brand : "");
  tally();
}

/* the only thing a tick changes outside its own row */
function tally() {
  if (!root) return;
  var n = 0; for (var k in picked) if (picked[k]) n++;
  var btn = root.querySelector(".fdsfc-pk__save");
  /* "Empty the bag" is truthful with nothing ticked, but on the example bag it
     is the FIRST thing a visitor reads, and it makes a green button look like
     a threat to a bag they have not built yet. Name what it actually does to
     what is actually on the chart. */
  btn.textContent = n ? "Update bag (" + n + ")"
    : onDemoBag() ? "Clear the example bag" : "Empty the bag";
  btn.setAttribute("data-n", n);
}


/* ---------------------------------------------------------------- CALIBRATION
   Ryan: "maybe we ask them to pick a disc and distance first?" and then, when
   I said the chart could not know how far they throw, "we can make it set up
   the chart here. maybe even have the line with slider for this disc? (with
   skip button too)".

   That is what this is. It is not a second copy of the chart's controls -- it
   DRIVES them, by setting the engine's own #fdsRefMold and #fdsPower and
   firing the events a person's own typing would fire. The engine has no idea
   the picker exists, and there is no state to keep in sync.

   Skippable, and it says so. Someone who came here to add a Firebird should
   not have to answer a question about their arm first. */
function calDiscs() {
  /* The reference disc has to be something the visitor has actually thrown, so
     the offer is the well-known moulds, most-stocked brands first, and never
     anything very understable -- an understable disc flips rather than running
     out of power, so it is a bad ruler. The chart says as much when you pick
     one; better not to offer it here at all. */
  var ok = pool().filter(function (d) { return d.tu + d.f > -2; });
  var rank = window.__FDS_BRANDS__ ? window.__FDS_BRANDS__.rank.slice(0, 6)
    .map(function (r) { return r.v; }) : [];
  return ok.filter(function (d) { return rank.indexOf(d.v) >= 0; })
    .sort(function (a, b) {
      return rank.indexOf(a.v) - rank.indexOf(b.v) || a.m.localeCompare(b.m);
    });
}

function setPower(metres) {
  var el = document.getElementById("fdsPower");
  if (!el) return;
  el.value = metres;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function setRef(slugv) {
  var el = document.getElementById("fdsRefMold");
  if (!el) return;
  el.value = slugv;
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

/* ---- UNITS ------------------------------------------------------------
   The chart already owns a m/ft toggle (.fdsfc-unit in the shell, `z` in
   chart.js). This panel used to ignore it entirely and speak metres at
   everyone, which was not merely inconsistent -- it was WRONG. setPower()
   writes into #fdsPower, and chart.js reads that field in whatever unit is
   currently selected. So with ft active, a visitor who set "110" in here was
   telling the chart 110 FEET: a 106 m arm recorded as 33 m, and every disc on
   the page redrawn for a player who does not exist.

   One source of truth: whatever the chart's toggle says. Read it, never cache
   it -- the visitor can flip units in the panel behind this modal while it is
   open, and a cached value would silently go stale. */
var FT_PER_M = 3.280839895;

function unit() {
  var b = document.querySelector('.fdsfc-unit button[aria-pressed="true"]');
  return (b && b.getAttribute("data-unit")) === "ft" ? "ft" : "m";
}

/* The slider's own bounds, in the ACTIVE unit. Mirrors the shell's #fdsPower
   min/max so the two controls cannot disagree about what is throwable. */
function bounds() {
  return unit() === "ft" ? { lo: 60, hi: 650, step: 10 } : { lo: 20, hi: 200, step: 5 };
}

function fmt(v) { return Math.round(v) + (unit() === "ft" ? " ft" : " m"); }

function renderCal() {
  var box = root.querySelector(".fdsfc-pk__cal");
  var show = !calibrated() && !skipped;
  box.hidden = !show;
  if (!show) { box.innerHTML = ""; return; }

  var refEl = document.getElementById("fdsRefMold");
  var cur = refEl ? refEl.value : "";
  var offer = calDiscs();
  var pw = document.getElementById("fdsPower");
  var b = bounds();
  /* #fdsPower is ALREADY in the active unit -- chart.js rewrites its value on
     every toggle -- so this is a straight read with no conversion. The
     fallback is the chart's own 106 m default expressed in that unit. */
  var dflt = unit() === "ft" ? 348 : 106;
  var dist = pw ? (parseFloat(pw.value) || dflt) : dflt;
  if (dist < b.lo) dist = b.lo;
  if (dist > b.hi) dist = b.hi;

  box.innerHTML =
    /* ASK FOR THE THROW THEY REPEAT, NOT THE ONE THEY REMEMBER.
       Disc golfers quote a personal best -- downhill, tailwind, three years
       ago -- because that is how the sport talks about itself. Take that
       number at face value and the chart draws discs holding lines the
       thrower has never held, then recommends overstable drivers they cannot
       turn over. The qualifier is not a caveat on the question, it IS the
       question. */
    "<h4>First &mdash; how far is a normal drive?</h4>" +
    "<p>Not your best ever &mdash; the controlled one you would throw on a tight " +
      "hole. Every distance below is scaled to it. Nearest is fine.</p>" +
    '<div class="fdsfc-pk__calrow">' +
      '<label for="fdsPkRef">I throw a</label>' +
      '<select id="fdsPkRef">' +
        offer.map(function (d) {
          var v = slug(d);
          return '<option value="' + esc(v) + '"' + (v === cur ? " selected" : "") + ">" +
            esc(d.m) + " &middot; " + esc(d.v) + "</option>";
        }).join("") +
      "</select>" +
      '<label for="fdsPkDist">about</label>' +
      '<output class="fdsfc-pk__calm" id="fdsPkOut">' + fmt(dist) + "</output>" +
    "</div>" +
    /* NO SHORTCUT ROW. v6 shipped five fixed metre buttons above this slider.
       They were removed because a fixed ladder cannot be right: it ignored the
       unit toggle, it ignored WHICH disc was in the select beside it -- 110 m
       with a Destroyer is an average arm, 110 m with a putter is a
       professional -- and its 70 m floor sat above the entire schools and
       fundraiser audience this shop actually serves. The slider asks one
       honest question and gets one honest answer. */
    '<input type="range" id="fdsPkDist" min="' + b.lo + '" max="' + b.hi + '" ' +
      'step="' + b.step + '" value="' + Math.round(dist) + '" ' +
      'aria-label="How far you throw that disc on a controlled drive">' +
    '<div class="fdsfc-pk__calends"><span>' + fmt(b.lo) + "</span>" +
      "<span>" + fmt(b.hi) + "</span></div>" +
    '<p class="fdsfc-pk__calsay" aria-live="polite"></p>' +
    '<div class="fdsfc-pk__calft">' +
      '<button type="button" class="fdsfc-pk__calskip">Skip &mdash; just show me the discs</button>' +
      '<button type="button" class="fdsfc-pk__caldone">Set the chart to this</button>' +
    "</div>";

  say();
}

/* Echo the engine's own verdict rather than writing a second one. If the two
   ever disagreed the user would have no way to tell which was lying. */
function say() {
  var out = root && root.querySelector(".fdsfc-pk__calsay");
  var src = document.getElementById("fdsPowerOut");
  if (!out || !src) return;
  out.innerHTML = src.innerHTML;
}

function build() {
  root = document.createElement("div");
  root.className = "fdsfc-pk"; root.id = "fdsPicker";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.setAttribute("aria-label", "Build your bag");
  root.innerHTML =
    '<div class="fdsfc-pk__back" data-close></div>' +
    '<div class="fdsfc-pk__box">' +
      '<div class="fdsfc-pk__hd">' +
        "<div><b>Build your bag</b><span>Tick everything you throw. Discs already in your bag are ticked &mdash; untick one to drop it. Nothing changes until you press update.</span></div>" +
        '<button type="button" class="fdsfc-pk__x" data-close aria-label="Close">&times;</button>' +
      "</div>" +
      /* Everything between the header and the footer is ONE scroller. See the
         note on .fdsfc-pk__body: the list used to be the only thing that
         scrolled, so a tall calibration panel starved it of height and the
         catalogue became unreachable. */
      '<div class="fdsfc-pk__body">' +
      '<div class="fdsfc-pk__cal" hidden></div>' +
      '<div class="fdsfc-pk__tools">' +
        '<input type="search" class="fdsfc-pk__q" placeholder="Search discs…" aria-label="Search discs" autocomplete="off">' +
        '<div class="fdsfc-pk__sort" role="group" aria-label="Sort">' +
          ["type", "speed", "brand", "name", "stability"].map(function (s) {
            return '<button type="button" data-sort="' + s + '" aria-pressed="' +
              (s === "type") + '">' + (s === "type" ? "By type" : s) + "</button>";
          }).join("") +
        "</div>" +
        '<label class="fdsfc-pk__gap"><input type="checkbox" class="fdsfc-pk__gapin"> Only discs that fill a gap</label>' +
      "</div>" +
      '<div class="fdsfc-pk__brands"></div>' +
      '<div class="fdsfc-pk__count" aria-live="polite"></div>' +
      '<div class="fdsfc-pk__list"></div>' +
      "</div>" +
      '<div class="fdsfc-pk__ft">' +
        '<button type="button" class="fdsfc-pk__cancel" data-close>Cancel</button>' +
        '<button type="button" class="fdsfc-pk__save">Update bag</button>' +
      "</div>" +
    "</div>";
  /* MUST live inside .fdsfc, not on <body>. Every colour token -- --bg, --acc,
     --ink, the lot -- is declared on the .fdsfc container so the chart can be
     dropped into a host page without leaking variables into it. Appending the
     modal to <body> put it outside that scope, so every var() resolved to
     nothing: transparent panels, invisible backdrop, the page showing straight
     through. position:fixed still pins it to the viewport from in here. */
  (document.getElementById("fdsfc") || document.body).appendChild(root);

  root.addEventListener("click", function (e) {
    var t = e.target;
    if (t.closest("[data-close]")) { close(); return; }
    var r = t.closest(".fdsfc-pk__row");
    if (r) {
      var s = r.getAttribute("data-slug");
      picked[s] = !picked[s];
      if (!picked[s]) delete picked[s];
      /* Toggle IN PLACE. A full re-render on every tick throws away the
         scroll position, which makes ticking six discs in a row an exercise
         in finding your place again -- and it gets worse the bigger the
         catalogue gets. The "In your bag" group re-sorts on next open, not
         mid-edit; moving a row out from under the cursor as you click it is
         its own kind of rude. */
      r.toggleAttribute("data-on", !!picked[s]);
      r.setAttribute("aria-checked", !!picked[s]);
      r.querySelector("i").innerHTML = picked[s] ? "&#10003;" : "";
      tally(); return;
    }
    if (t.closest(".fdsfc-pk__calskip")) { skipped = true; renderCal(); render(); return; }
    if (t.closest(".fdsfc-pk__caldone")) {
      /* Pressing done is what puts p= in the hash, which is also what makes
         calibrated() true -- so the panel closes because the answer exists,
         not because a button was pressed. */
      var d = root.querySelector("#fdsPkDist");
      if (d) setPower(parseInt(d.value, 10));
      var r = root.querySelector("#fdsPkRef");
      if (r) setRef(r.value);
      calSet = true;
      renderCal(); render(); return;
    }
    var sb = t.closest("[data-sort]");
    if (sb) {
      sort = sb.getAttribute("data-sort");
      Array.prototype.forEach.call(root.querySelectorAll("[data-sort]"), function (b) {
        b.setAttribute("aria-pressed", b === sb ? "true" : "false");
      });
      render(); return;
    }
    if (t.closest(".fdsfc-pk__save")) { commit(); return; }
  });
  /* The rail lives HERE and not above the chart. Ryan, on the first
     placement: "where you're suggesting will take away from the flight chart
     experience." Inside the picker it is answering a question the user is
     already asking -- "show me your Innovas" while building a bag -- instead
     of standing between them and the flight paths. */
  if (window.__FDS_BRANDS__) {
    rail = window.__FDS_BRANDS__.mount(root.querySelector(".fdsfc-pk__brands"), {
      allLabel: "All brands",
      onPick: function (v) { brand = v; brandAsked = false; render(); }
    });
  }

  root.addEventListener("input", function (e) {
    if (e.target.id !== "fdsPkDist") return;
    var v = parseInt(e.target.value, 10);
    var o = root.querySelector("#fdsPkOut");
    if (o) o.textContent = fmt(v);
    /* Straight through, no conversion: the slider and #fdsPower are both in
       the active unit, and chart.js converts on read. Converting here would
       apply the factor twice. */
    setPower(v); say();
  });
  root.addEventListener("change", function (e) {
    if (e.target.id !== "fdsPkRef") return;
    setRef(e.target.value); say();
  });

  root.querySelector(".fdsfc-pk__q").addEventListener("input", function (e) {
    q = e.target.value.trim(); render();
  });
  root.querySelector(".fdsfc-pk__gapin").addEventListener("change", function (e) {
    gapsOnly = e.target.checked; render();
  });
  document.addEventListener("keydown", function (e) {
    if (open && e.key === "Escape") close();
  });
}

/* Commit rebuilds the token list from what is ticked, and PRESERVES the throw
   suffix of anything that was already there. A bulk edit that silently reset
   somebody's forehand overrides would be worse than no bulk edit. */
function commit() {
  var was = hashTokens(), keptByBase = {};
  was.forEach(function (t) { (keptByBase[base(t)] = keptByBase[base(t)] || []).push(t); });
  var out = [];
  was.forEach(function (t) { if (picked[base(t)]) out.push(t); });
  Object.keys(picked).forEach(function (s) {
    if (!keptByBase[s] && BY[s]) out.push(s);
  });
  writeBag(out);
  close();
}

/* IS THE BAG ON SCREEN STILL THE DEMO WE HANDED THEM?
   The chart already answers this on every render, because "Reset to example
   bag" only makes sense when the bag is NOT the example bag -- so that button
   is hidden exactly when it is. Reading its state is a coupling to rendered
   DOM, and a deliberate one: the alternative is a second copy of the example
   bag list over here, which would drift the first time the bag changed.

   FAILS SAFE. If the button is missing the answer is "no", which means the
   picker opens pre-ticked -- the old, non-destructive behaviour. A regression
   here costs Ryan's clear-the-demo rule, never somebody's bag.
   verify-picker.mjs asserts both halves so it cannot rot quietly. */
function onDemoBag() {
  var rb = document.getElementById("fdsResetBag");
  return !!rb && rb.hidden;
}

function openPicker() {
  if (!root) build();
  picked = {};
  /* Ryan: "when someone goes to build the bag... I want all of the other
     populated ones to disappear." The example bag is a DEMO, not their bag.
     Someone pressing Build your bag is telling us they want to build one, and
     handing them twelve pre-ticked Innovas is us answering a question they did
     not ask -- they then have to untick our suggestions before they can start.

     So on the untouched demo bag the picker opens EMPTY, and pressing update
     replaces the demo with whatever they ticked. The moment the bag is
     genuinely theirs the old rule applies again and it opens pre-ticked,
     because reopening the picker must never cost somebody work they did. */
  var fresh = onDemoBag();
  if (!fresh) hashTokens().forEach(function (t) { picked[base(t)] = true; });
  var hd = root.querySelector(".fdsfc-pk__hd span");
  if (hd) hd.innerHTML = fresh
    ? "The bag on the chart is just an example &mdash; it comes off as soon as you update. Tick everything you throw. Nothing changes until you press update."
    : "Tick everything you throw. Discs already in your bag are ticked &mdash; untick one to drop it. Nothing changes until you press update.";
  q = ""; gapsOnly = false; sort = "type";
  root.querySelector(".fdsfc-pk__q").value = "";
  root.querySelector(".fdsfc-pk__gapin").checked = false;
  /* The map's brand carries in, but it ASKS. Silently widening throws away what
     they just told the map; silently inheriting hides 1,487 moulds. */
  var MAP = window.__FDS_MAP__;
  brand = (MAP && MAP.brand && MAP.brand()) || "";
  if (rail) brand ? rail.set(brand) : rail.clear();
  brandAsked = !!brand;
  renderCal();
  Array.prototype.forEach.call(root.querySelectorAll("[data-sort]"), function (b) {
    b.setAttribute("aria-pressed", b.getAttribute("data-sort") === "type" ? "true" : "false");
  });
  open = true; root.setAttribute("data-open", "");
  document.documentElement.style.overflow = "hidden";
  render();
  root.querySelector(".fdsfc-pk__q").focus();
}
function close() {
  open = false;
  if (root) root.removeAttribute("data-open");
  document.documentElement.style.overflow = "";
}

function boot() {
  Array.prototype.forEach.call(document.querySelectorAll("[data-fds-picker]"), function (b) {
    b.addEventListener("click", function (e) { e.preventDefault(); openPicker(); });
  });
}
window.__FDS_PICKER__ = { open: openPicker, close: close };
/* Test hook. The picker's brand and query are deliberately module-local view
   state, but a test that drives them through DOM events ends up asserting on
   the rail's markup rather than on the list's behaviour -- and then breaks
   every time the rail is restyled. This exposes the two setters and nothing
   else, so the tests can ask "what does the list do when the brand is Innova"
   without knowing how a brand gets picked. */
window.__FDS_PK_TEST__ = {
  setBrand: function (v) { brand = v || ""; brandAsked = false; if (rail) v ? rail.set(v) : rail.clear(); render(); },
  setQuery: function (v) { q = v || ""; var i = root && root.querySelector(".fdsfc-pk__q"); if (i) i.value = q; render(); }
};
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();